import { ReactNode } from 'react';

import { styled } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { Field } from 'react-final-form';

import { Typography, Box, Stack, tokens } from '@cast/design-system';
import { RffChipInput, RffInput, RffSelect } from '@cast/shared-rff-ui';

import { LabelExpressionOperatorOption, LabelExpressionValue } from './types';

const FieldPaper = styled(Stack)({
  padding: 8,
  backgroundColor: tokens.theme.bg.structural.surfaceRaised,
  borderRadius: '4px',
  rowGap: 8,
});

type LabelExpressionRowProps<TOperator> = {
  field: string;
  operatorOptions: LabelExpressionOperatorOption<TOperator>[];
  defaultOperator: TOperator;
  isKeyRequired?: boolean | ((operator: TOperator) => boolean);
  isValueHidden: (operator: TOperator) => boolean;
  keyTooltip?: string;
  removeButton: ReactNode;
};

export const LabelExpressionRow = <TOperator,>({
  field,
  operatorOptions,
  defaultOperator,
  isKeyRequired = true,
  isValueHidden,
  keyTooltip,
  removeButton,
}: LabelExpressionRowProps<TOperator>) => {
  return (
    <Field<LabelExpressionValue<TOperator>>
      name={field}
      subscription={{ value: true }}
    >
      {({ input }) => {
        const operator = input.value?.operator ?? defaultOperator;
        const keyRequired =
          typeof isKeyRequired === 'function'
            ? isKeyRequired(operator)
            : isKeyRequired;

        return (
          <FieldPaper>
            <Stack direction="row" alignItems="center">
              <Stack rowGap={12} flex={1}>
                <Box
                  display="grid"
                  gridTemplateColumns="2fr 1fr"
                  gap={8}
                  alignItems="flex-start"
                >
                  <RffInput
                    testId="key"
                    name={`${field}.key`}
                    fieldProps={{
                      isEqual: (
                        previous: string | null | undefined,
                        current: string | null | undefined
                      ) => {
                        if (previous === undefined && current === '') {
                          return true;
                        }
                        return previous === current;
                      },
                    }}
                    labelTooltipProps={
                      !keyRequired && keyTooltip
                        ? { title: keyTooltip }
                        : undefined
                    }
                    label={
                      <>
                        Key
                        {keyRequired && (
                          <Typography
                            color="red.400"
                            variant="P12M"
                            component="span"
                          >
                            *
                          </Typography>
                        )}
                      </>
                    }
                    placeholder="e.g. environment"
                    success={false}
                  />
                  <RffSelect
                    testId="operator"
                    name={`${field}.operator`}
                    options={operatorOptions}
                    optionLabel={(o) => o.label}
                    optionValue={(o) => o.value}
                    label="Operator"
                    applySortingWhileSearching={false}
                    inputProps={{
                      success: false,
                    }}
                  />
                </Box>
                {!isValueHidden(operator) && (
                  <RffChipInput
                    testId="value"
                    name={`${field}.value`}
                    required
                    label={
                      <>
                        Value
                        <Typography
                          color="red.400"
                          variant="P12M"
                          component="span"
                        >
                          *
                        </Typography>
                      </>
                    }
                    fieldProps={{
                      isEqual: (
                        previous: string[] | undefined,
                        current: string[] | undefined
                      ) => {
                        if (previous === undefined && isEqual(current, [])) {
                          return true;
                        }
                        return isEqual(previous, current);
                      },
                    }}
                    hint="Enter comma-separated values"
                    separators={['Enter', ',', ' ']}
                    showClearButton={false}
                    success={false}
                  />
                )}
              </Stack>
              {removeButton}
            </Stack>
          </FieldPaper>
        );
      }}
    </Field>
  );
};
